import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';
import { translations, stepsTranslations } from '../translations/content';
import Auth from './Auth';
import supabase from '../supabase/supabase';

// DnD Kit Imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const { 
  FiArrowLeft, FiArrowRight, FiCheckCircle, FiTarget, 
  FiLock, FiRocket, FiZap, FiShield, FiMail, FiUserPlus,
  FiSave, FiLoader, FiLogIn, FiChevronDown, FiChevronUp,
  FiMessageCircle, FiPlus, FiTrash2, FiBookOpen, FiEdit3,
  FiSmile, FiFrown, FiLayers, FiMoreVertical, FiUsers,
  FiUser
} = FiIcons;

// Table constants
const STEP_2_TABLE = 'step_2_knowledge_mapping_1775471543474';
const STEP_4_TABLE = 'step_4_avatars_1775474655253';

// --- Sortable Item Component for Step 2 ---
const SortableTopicItem = ({ topic, number, isActive, onClick, onDelete, onAddSubtopic }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: topic.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 1,
    opacity: isDragging ? 0.5 : 1
  };

  const depth = (number.toString().match(/\./g) || []).length;

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`group flex items-center justify-between p-4 rounded-2xl cursor-pointer transition-all border-2 ${
        isActive 
          ? 'bg-blue-50 text-blue-700 border-blue-200 shadow-md ring-2 ring-blue-100 ring-offset-2' 
          : 'bg-white hover:bg-gray-50 text-gray-600 border-gray-100 hover:border-gray-200'
      }`}
      onClick={onClick}
    >
      <div className="flex items-center space-x-4 flex-grow min-w-0" style={{ marginLeft: `${depth * 24}px` }}>
        <div 
          {...attributes} 
          {...listeners} 
          className="cursor-grab active:cursor-grabbing p-2 text-gray-300 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <SafeIcon icon={FiMoreVertical} className="text-lg" />
        </div>
        <div className="flex items-center space-x-3 overflow-hidden">
          <span className="text-sm font-black text-blue-500 bg-blue-50 px-2 py-1 rounded-lg flex-shrink-0 min-w-[2.5rem] text-center shadow-sm">
            {number}
          </span>
          <span className="text-base font-bold truncate">{topic.topic || '...'}</span>
        </div>
      </div>
      
      <div className="flex items-center space-x-2 ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
        <button 
          onClick={(e) => { e.stopPropagation(); onAddSubtopic(topic.id); }}
          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded-xl transition-all shadow-sm bg-white border border-gray-100"
          title="Add Subtopic"
        >
          <SafeIcon icon={FiPlus} className="text-sm" />
        </button>
        <button 
          onClick={(e) => { e.stopPropagation(); onDelete(topic.id); }}
          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-100 rounded-xl transition-all shadow-sm bg-white border border-gray-100"
          title="Delete Topic"
        >
          <SafeIcon icon={FiTrash2} className="text-sm" />
        </button>
      </div>
    </div>
  );
};

const StepDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const t = translations[lang].detail;
  const p = translations[lang].paths;
  const steps = stepsTranslations[lang];
  const stepId = parseInt(id);
  const step = steps.find(s => s.id === stepId);

  const [hasUnlockedAccess, setHasUnlockedAccess] = useState(false);
  const [user, setUser] = useState(null);
  const [loadingAccess, setLoadingAccess] = useState(true);

  // Refs
  const editorRef = useRef(null);
  const topicInputRef = useRef(null);

  // Step 1 Vision State
  const [step1Vision, setStep1Vision] = useState('');
  const [isSavingStep1, setIsSavingStep1] = useState(false);
  const [isSavedStep1, setIsSavedStep1] = useState(false);
  const [isStep1DropdownOpen, setIsStep1DropdownOpen] = useState(false);

  // Step 2 Knowledge State
  const [step2Topics, setStep2Topics] = useState([]);
  const [activeTopicId, setActiveTopicId] = useState(null);
  const [isSavingStep2, setIsSavingStep2] = useState(false);
  const [isSavedStep2, setIsSavedStep2] = useState(false);
  const [isCreatingTopic, setIsCreatingTopic] = useState(false);

  // Step 3 Idea State
  const [step3Data, setStep3Data] = useState({
    problem_solved: '',
    target_group: '',
    customer_needs: '',
    suitability: '',
    product_name_slogan: ''
  });
  const [isSavingStep3, setIsSavingStep3] = useState(false);
  const [isSavedStep3, setIsSavedStep3] = useState(false);

  // Step 4 Avatar State
  const [step4Avatars, setStep4Avatars] = useState([]);
  const [activeAvatarId, setActiveAvatarId] = useState(null);
  const [isSavingStep4, setIsSavingStep4] = useState(false);
  const [isSavedStep4, setIsSavedStep4] = useState(false);

  // Step 7 Offer State
  const [step7Data, setStep7Data] = useState({
    product_description: '',
    target_group: '',
    goal: '',
    shortcut: '',
    journey_before: '',
    journey_after: ''
  });
  const [isSavingStep7, setIsSavingStep7] = useState(false);
  const [isSavedStep7, setIsSavedStep7] = useState(false);

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const checkUser = async () => {
      setLoadingAccess(true);
      const { data: { session } } = await supabase.auth.getSession();
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      setHasUnlockedAccess(!!currentUser);
      setLoadingAccess(false);
    };
    checkUser();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      setHasUnlockedAccess(!!currentUser);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      if (stepId === 1) {
        const { data } = await supabase.from('step_1_vision_1773100000000').select('vision_text').eq('user_id', user.id).single();
        if (data) setStep1Vision(data.vision_text);
      } else if (stepId === 2) {
        const { data } = await supabase.from(STEP_2_TABLE).select('*').eq('user_id', user.id).order('sort_order', { ascending: true });
        if (data && data.length > 0) {
          setStep2Topics(data);
          setActiveTopicId(data[0].id);
        } else {
          setStep2Topics([]);
        }
      } else if (stepId === 3) {
        const { data } = await supabase.from('step_3_ideas_1772902600000').select('*').eq('user_id', user.id).single();
        if (data) setStep3Data(data);
      } else if (stepId === 4) {
        const { data } = await supabase.from(STEP_4_TABLE).select('*').eq('user_id', user.id).order('created_at', { ascending: true });
        if (data && data.length > 0) {
          setStep4Avatars(data);
          setActiveAvatarId(data[0].id);
        }
      } else if (stepId === 7) {
        const { data } = await supabase.from('step_7_offers_1773460000000').select('*').eq('user_id', user.id).single();
        if (data) setStep7Data(data);
      }
    };
    fetchData();
  }, [stepId, user]);

  // Step 2 Helpers
  const buildTopicTree = (items, parentId = null, prefix = '') => {
    return items
      .filter(item => item.parent_id === parentId)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((item, index) => {
        const number = prefix ? `${prefix}.${index + 1}` : `${index + 1}`;
        return {
          ...item,
          number,
          children: buildTopicTree(items, item.id, number)
        };
      });
  };

  const flattenedTree = useMemo(() => {
    const tree = buildTopicTree(step2Topics);
    const flatten = (nodes) => {
      let result = [];
      nodes.forEach(node => {
        const { children, ...rest } = node;
        result.push(rest);
        if (children && children.length > 0) {
          result = [...result, ...flatten(children)];
        }
      });
      return result;
    };
    return flatten(tree);
  }, [step2Topics]);

  const handleAddStep2Topic = async (parentId = null) => {
    if (!user || isCreatingTopic) return;
    setIsCreatingTopic(true);
    const defaultTitle = lang === 'de' ? 'Neues Thema' : 'New Topic';
    try {
      const { data, error } = await supabase.from(STEP_2_TABLE).insert({
        user_id: user.id, topic: defaultTitle, content: '', parent_id: parentId,
        sort_order: step2Topics.filter(t => t.parent_id === parentId).length
      }).select().single();
      if (error) throw error;
      if (data) {
        setStep2Topics(prev => [...prev, data]);
        setActiveTopicId(data.id);
        setTimeout(() => {
          topicInputRef.current?.focus();
          editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    } catch (err) { console.error('Error adding topic:', err); } finally { setIsCreatingTopic(false); }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setStep2Topics((items) => {
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);
      const newArray = arrayMove(items, oldIndex, newIndex);
      return newArray.map((item, index) => ({ ...item, sort_order: index }));
    });
  };

  const handleUpdateStep2Content = (id, content) => {
    setStep2Topics(prev => prev.map(t => t.id === id ? { ...t, content } : t));
  };

  const handleDeleteStep2Topic = async (id) => {
    try {
      const { error } = await supabase.from(STEP_2_TABLE).delete().eq('id', id);
      if (error) throw error;
      const filtered = step2Topics.filter(t => t.id !== id && t.parent_id !== id);
      setStep2Topics(filtered);
      if (activeTopicId === id) { setActiveTopicId(filtered.length > 0 ? filtered[0].id : null); }
    } catch (err) { console.error('Error deleting topic:', err); }
  };

  const handleStep2Save = async () => {
    if (!user) return;
    setIsSavingStep2(true);
    try {
      const { error } = await supabase.from(STEP_2_TABLE).upsert(
        step2Topics.map(t => ({ 
          id: t.id, user_id: t.user_id, topic: t.topic, content: t.content,
          parent_id: t.parent_id, sort_order: t.sort_order, updated_at: new Date().toISOString() 
        }))
      );
      if (error) throw error;
      setIsSavedStep2(true);
      setTimeout(() => setIsSavedStep2(false), 3000);
    } catch (err) { console.error('Error saving knowledge:', err); } finally { setIsSavingStep2(false); }
  };

  // Step 4 Helpers
  const handleAddAvatar = async () => {
    if (!user) return;
    const newAvatar = { user_id: user.id, name: lang === 'de' ? 'Neuer Avatar' : 'New Avatar', demographics: '', motivations: '', challenges: '' };
    const { data, error } = await supabase.from(STEP_4_TABLE).insert(newAvatar).select().single();
    if (data) {
      setStep4Avatars([...step4Avatars, data]);
      setActiveAvatarId(data.id);
    }
  };

  const handleSaveAvatar = async () => {
    if (!user || !activeAvatarId) return;
    setIsSavingStep4(true);
    const activeAvatar = step4Avatars.find(a => a.id === activeAvatarId);
    const { error } = await supabase.from(STEP_4_TABLE).upsert({ ...activeAvatar, updated_at: new Date().toISOString() });
    setIsSavingStep4(false);
    if (!error) { setIsSavedStep4(true); setTimeout(() => setIsSavedStep4(false), 3000); }
  };

  const handleDeleteAvatar = async (id) => {
    const { error } = await supabase.from(STEP_4_TABLE).delete().eq('id', id);
    if (!error) {
      const filtered = step4Avatars.filter(a => a.id !== id);
      setStep4Avatars(filtered);
      if (activeAvatarId === id) setActiveAvatarId(filtered.length > 0 ? filtered[0].id : null);
    }
  };

  // Save Handlers for other steps
  const handleStep1Save = async () => {
    if (!user) return;
    setIsSavingStep1(true);
    const { error } = await supabase.from('step_1_vision_1773100000000').upsert({ user_id: user.id, vision_text: step1Vision, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
    setIsSavingStep1(false);
    if (!error) { setIsSavedStep1(true); setTimeout(() => setIsSavedStep1(false), 3000); }
  };

  const handleStep3Save = async () => {
    if (!user) return;
    setIsSavingStep3(true);
    const { error } = await supabase.from('step_3_ideas_1772902600000').upsert({ user_id: user.id, ...step3Data, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
    setIsSavingStep3(false);
    if (!error) { setIsSavedStep3(true); setTimeout(() => setIsSavedStep3(false), 3000); }
  };

  const handleStep7Save = async () => {
    if (!user) return;
    setIsSavingStep7(true);
    const { error } = await supabase.from('step_7_offers_1773460000000').upsert({ user_id: user.id, ...step7Data, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
    setIsSavingStep7(false);
    if (!error) { setIsSavedStep7(true); setTimeout(() => setIsSavedStep7(false), 3000); }
  };

  const renderLockedOverlay = () => (
    <div className="absolute inset-x-0 top-0 bottom-0 z-20 flex items-center justify-center p-6 bg-blue-900/10 backdrop-blur-[12px] rounded-[2.5rem]">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white/95 p-8 md:p-10 rounded-[2.5rem] shadow-2xl border border-blue-100 max-w-lg w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner"><SafeIcon icon={FiLock} className="text-3xl text-blue-600" /></div>
          <h4 className="text-2xl font-black text-gray-900 mb-3 uppercase tracking-tight">{t.lockedTitle}</h4>
          <p className="text-gray-600 font-medium">{t.authPrompt}</p>
        </div>
        <div className="bg-gray-50/50 p-6 rounded-[2rem] border border-gray-100 shadow-inner"><Auth embedded={true} initialIsLogin={false} /></div>
      </motion.div>
    </div>
  );

  if (!step) return null;

  const activeTopic = step2Topics.find(t => t.id === activeTopicId);
  const activeAvatar = step4Avatars.find(a => a.id === activeAvatarId);

  return (
    <div className="min-h-screen bg-gray-50 pb-20 pt-24">
      <div className="max-w-6xl mx-auto px-4 mb-20">
        <div className="flex items-center justify-between mb-8">
          <Link to={p.steps} className="group flex items-center space-x-2 text-gray-500 hover:text-blue-600 transition-colors bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100 italic">
            <SafeIcon icon={FiArrowLeft} className="transition-transform group-hover:-translate-x-1" /><span className="text-sm font-bold">{t.back}</span>
          </Link>
          <span className="text-sm font-bold text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{t.stepOf.replace('{current}', stepId)}</span>
        </div>

        <div className="bg-white rounded-[2.5rem] p-8 md:p-12 mb-8 shadow-sm border border-gray-100 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none font-black text-[200px] leading-none text-blue-900">{stepId}</div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10">
            <div className="inline-flex items-center space-x-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-6"><SafeIcon icon={FiTarget} /><span>{t.overview}</span></div>
            <h1 className="text-4xl md:text-6xl font-black text-gray-900 mb-4 tracking-tight leading-tight">{step.title}</h1>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-[2.5rem] p-8 md:p-10 shadow-sm border border-gray-100 relative">
              <h2 className="text-2xl font-black text-gray-900 mb-6 flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center"><SafeIcon icon={FiTarget} className="text-blue-600" /></div>
                <span>{t.overview}</span>
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed font-medium">{step.description}</p>
            </div>

            <div className="relative">
              <div className={`space-y-8 transition-all duration-700 ${!hasUnlockedAccess ? 'blur-xl grayscale opacity-30 select-none' : ''}`}>
                
                {/* Step 1 Tool */}
                {stepId === 1 && (
                  <div className="space-y-8">
                    <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem]">
                      <h3 className="text-xl font-black text-gray-900 mb-6 italic">{t.step1IntroTitle}</h3>
                      <div className="border border-gray-100 rounded-3xl overflow-hidden">
                        <button onClick={() => setIsStep1DropdownOpen(!isStep1DropdownOpen)} className="w-full flex items-center justify-between p-6 bg-gray-50 hover:bg-gray-100 transition-colors">
                          <span className="text-lg font-black text-gray-900">{t.step1DropdownTitle}</span>
                          <SafeIcon icon={isStep1DropdownOpen ? FiChevronUp : FiChevronDown} className="text-blue-600 text-xl" />
                        </button>
                        <AnimatePresence>{isStep1DropdownOpen && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden bg-white border-t border-gray-100">
                            <div className="p-8 space-y-8">
                              {t.step1DropdownItems.map((item, i) => (
                                <div key={i} className="space-y-3">
                                  <h4 className="text-lg font-black text-blue-600">{item.title}</h4>
                                  <p className="text-gray-600 leading-relaxed font-medium">{item.content}</p>
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}</AnimatePresence>
                      </div>
                    </div>
                    <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem]">
                      <div className="flex items-center space-x-3 mb-8"><div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center"><SafeIcon icon={FiRocket} className="text-blue-600 text-xl" /></div><h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step1ToolTitle}</h3></div>
                      <div className="mb-8 p-8 bg-blue-50 rounded-3xl border-2 border-blue-100 shadow-inner">
                        <p className="text-lg font-bold text-blue-900 leading-relaxed italic mb-4">"{t.step1VisionQuestion}"</p>
                        <p className="text-sm font-black text-blue-400 uppercase tracking-widest underline decoration-2 underline-offset-4">{t.step1VisionLeadIn}</p>
                      </div>
                      <textarea value={step1Vision} onChange={(e) => setStep1Vision(e.target.value)} placeholder={t.step1Placeholder} className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl p-6 focus:border-blue-500 focus:ring-0 transition-all min-h-[250px] font-medium text-gray-700 shadow-inner text-lg leading-relaxed" />
                      <div className="pt-4 flex items-center justify-end">
                        <button onClick={handleStep1Save} className={`flex items-center space-x-2 px-10 py-4 rounded-full font-black transition-all ${isSavedStep1 ? 'bg-green-500 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                          {isSavingStep1 ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={isSavedStep1 ? FiCheckCircle : FiSave} />}
                          <span>{isSavingStep1 ? t.step1Saving : (isSavedStep1 ? t.step1Saved : t.step1Save)}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2 Tool */}
                {stepId === 2 && (
                  <div className="space-y-10">
                    <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem]">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
                        <div className="flex items-center space-x-4"><div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center shadow-inner"><SafeIcon icon={FiBookOpen} className="text-blue-600 text-2xl" /></div><div><h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step2ToolTitle}</h3><p className="text-xs font-bold text-blue-500 uppercase tracking-widest">{lang === 'de' ? 'Strukturiere dein Fachwissen' : 'Structure your domain knowledge'}</p></div></div>
                        <div className="flex items-center space-x-3">
                          <button onClick={() => handleAddStep2Topic(null)} disabled={isCreatingTopic} className="flex items-center space-x-2 px-6 py-4 rounded-2xl font-black bg-blue-50 text-blue-700 hover:bg-blue-100 transition-all text-sm border-2 border-blue-100 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed">
                            {isCreatingTopic ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiPlus} />}<span>{t.step2AddTopic}</span>
                          </button>
                          <button onClick={handleStep2Save} disabled={isSavingStep2 || step2Topics.length === 0} className={`flex items-center space-x-2 px-6 py-4 rounded-2xl font-black transition-all text-sm shadow-lg active:scale-95 ${isSavedStep2 ? 'bg-green-500 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                            {isSavingStep2 ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={isSavedStep2 ? FiCheckCircle : FiSave} />}<span>{isSavingStep2 ? t.step2Saving : (isSavedStep2 ? t.step2Saved : t.step2SaveAll)}</span>
                          </button>
                        </div>
                      </div>
                      <div className="bg-gray-50/50 p-4 md:p-8 rounded-[2.5rem] border-2 border-gray-100 shadow-inner">
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                          <SortableContext items={flattenedTree.map(i => i.id)} strategy={verticalListSortingStrategy}>
                            <div className="space-y-3">
                              {flattenedTree.length === 0 ? (
                                <div className="text-center py-12 bg-white rounded-3xl border-2 border-dashed border-gray-200">
                                  <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4"><SafeIcon icon={FiPlus} className="text-2xl text-blue-300" /></div>
                                  <p className="text-gray-400 font-bold italic mb-6">{t.step2Empty}</p>
                                  <button onClick={() => handleAddStep2Topic(null)} disabled={isCreatingTopic} className="inline-flex items-center space-x-2 px-8 py-4 bg-blue-600 text-white rounded-2xl font-black hover:bg-blue-700 transition-all shadow-lg active:scale-95 disabled:opacity-50">
                                    {isCreatingTopic ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiPlus} />}<span>{lang === 'de' ? 'Starte mit deinem ersten Thema' : 'Start with your first topic'}</span>
                                  </button>
                                </div>
                              ) : flattenedTree.map((topic) => (
                                <SortableTopicItem key={topic.id} topic={topic} number={topic.number} isActive={activeTopicId === topic.id} onClick={() => setActiveTopicId(topic.id)} onDelete={handleDeleteStep2Topic} onAddSubtopic={handleAddStep2Topic} />
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      </div>
                    </div>
                    <div ref={editorRef} className="scroll-mt-24">
                      <AnimatePresence mode="wait">
                        {activeTopic ? (
                          <motion.div key={activeTopicId} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="bg-white rounded-[2.5rem] border-2 border-blue-50 shadow-xl p-6 md:p-10 relative overflow-hidden">
                            <div className="relative z-10 flex flex-col space-y-8">
                              <div className="flex items-center space-x-6">
                                <div className="text-3xl font-black text-blue-600 bg-blue-50 w-16 h-16 flex items-center justify-center rounded-[1.5rem] shadow-sm flex-shrink-0">{activeTopic.number}</div>
                                <div className="flex-grow">
                                  <label className="block text-[10px] font-black text-blue-400 uppercase tracking-[0.2em] mb-1 ml-1">{lang === 'de' ? 'Themen-Überschrift' : 'Topic Heading'}</label>
                                  <input ref={topicInputRef} type="text" value={activeTopic.topic} onChange={(e) => setStep2Topics(prev => prev.map(t => t.id === activeTopicId ? { ...t, topic: e.target.value } : t))} className="text-2xl md:text-3xl font-black text-gray-900 border-none bg-transparent focus:ring-0 w-full p-0 placeholder-gray-200" placeholder={t.step2TopicPlaceholder} />
                                </div>
                              </div>
                              <div className="space-y-4">
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1 ml-1">{lang === 'de' ? 'Detaillierter Inhalt' : 'Detailed Content'}</label>
                                <textarea value={activeTopic.content} onChange={(e) => handleUpdateStep2Content(activeTopicId, e.target.value)} placeholder={t.step2ContentPlaceholder} className="w-full bg-gray-50/50 border-2 border-gray-100 rounded-[2rem] p-8 focus:border-blue-500 focus:ring-0 transition-all min-h-[400px] font-medium text-gray-700 shadow-inner leading-relaxed text-lg" />
                              </div>
                            </div>
                          </motion.div>
                        ) : <div className="min-h-[400px] flex flex-col items-center justify-center bg-gray-100/30 rounded-[2.5rem] border-2 border-dashed border-gray-200 text-center p-12"><div className="w-24 h-24 bg-white rounded-[2rem] shadow-sm flex items-center justify-center mb-8 text-gray-300"><SafeIcon icon={FiEdit3} className="text-4xl" /></div><h4 className="text-2xl font-black text-gray-400 mb-4">{t.step2Empty}</h4></div>}
                      </AnimatePresence>
                    </div>
                  </div>
                )}

                {/* Step 3 Tool */}
                {stepId === 3 && (
                  <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem]">
                    <div className="flex items-center space-x-3 mb-8"><div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center"><SafeIcon icon={FiRocket} className="text-blue-600 text-xl" /></div><h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step3ToolTitle}</h3></div>
                    <div className="mb-10 p-8 bg-blue-50 rounded-3xl border border-blue-100 italic text-gray-700 text-lg">{t.step3ExplanatoryText}</div>
                    <div className="space-y-6">
                      {[1,2,3,4,5].map(num => {
                        const keys = ['problem_solved', 'target_group', 'customer_needs', 'suitability', 'product_name_slogan'];
                        const field = keys[num-1];
                        return (
                          <div key={num} className="space-y-2">
                            <label className="block text-sm font-black text-gray-900 uppercase tracking-widest">{t[`step3Question${num}`]}</label>
                            <textarea value={step3Data[field] || ''} onChange={(e) => setStep3Data({ ...step3Data, [field]: e.target.value })} className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl p-4 focus:border-blue-500 focus:ring-0 transition-all min-h-[100px] font-medium text-gray-700 shadow-inner" />
                          </div>
                        );
                      })}
                      <div className="pt-4 flex items-center justify-end">
                        <button onClick={handleStep3Save} className={`flex items-center space-x-2 px-10 py-4 rounded-full font-black transition-all ${isSavedStep3 ? 'bg-green-500 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                          {isSavingStep3 ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={isSavedStep3 ? FiCheckCircle : FiSave} />}<span>{isSavingStep3 ? t.step3Saving : (isSavedStep3 ? t.step3Saved : t.step3Save)}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4 Tool - Customer Avatars */}
                {stepId === 4 && (
                  <div className="space-y-8">
                    <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem]">
                      <div className="flex items-center space-x-3 mb-8"><div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center"><SafeIcon icon={FiUsers} className="text-blue-600 text-xl" /></div><h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step4ToolTitle}</h3></div>
                      <div className="mb-10 p-8 bg-blue-50 rounded-3xl border border-blue-100 italic text-gray-700 text-lg leading-relaxed shadow-inner">{t.step4ExplanatoryText}</div>
                      
                      <div className="flex flex-wrap gap-3 mb-8">
                        {step4Avatars.map(avatar => (
                          <button key={avatar.id} onClick={() => setActiveAvatarId(avatar.id)} className={`px-6 py-3 rounded-2xl font-black flex items-center space-x-2 transition-all border-2 ${activeAvatarId === avatar.id ? 'bg-blue-600 text-white border-blue-600 shadow-lg' : 'bg-white text-gray-600 border-gray-100 hover:border-blue-200'}`}>
                            <SafeIcon icon={FiUser} /><span>{avatar.name || '...'}</span>
                          </button>
                        ))}
                        <button onClick={handleAddAvatar} className="px-6 py-3 rounded-2xl font-black flex items-center space-x-2 bg-blue-50 text-blue-700 border-2 border-dashed border-blue-200 hover:bg-blue-100 transition-all"><SafeIcon icon={FiPlus} /><span>{t.step4AddAvatar}</span></button>
                      </div>

                      <AnimatePresence mode="wait">
                        {activeAvatar ? (
                          <motion.div key={activeAvatarId} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6 bg-gray-50/50 p-8 rounded-[2.5rem] border border-gray-100">
                            <div className="grid md:grid-cols-2 gap-6">
                              <div className="space-y-2">
                                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest">{t.step4AvatarName}</label>
                                <input type="text" value={activeAvatar.name} onChange={(e) => setStep4Avatars(prev => prev.map(a => a.id === activeAvatarId ? { ...a, name: e.target.value } : a))} className="w-full bg-white border-2 border-gray-100 rounded-xl p-4 focus:ring-0 focus:border-blue-500 font-bold" />
                              </div>
                              <div className="space-y-2">
                                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest">{t.step4Demographics}</label>
                                <input type="text" value={activeAvatar.demographics} onChange={(e) => setStep4Avatars(prev => prev.map(a => a.id === activeAvatarId ? { ...a, demographics: e.target.value } : a))} className="w-full bg-white border-2 border-gray-100 rounded-xl p-4 focus:ring-0 focus:border-blue-500 font-bold" />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <label className="block text-xs font-black text-gray-400 uppercase tracking-widest">{t.step4Motivations}</label>
                              <textarea value={activeAvatar.motivations} onChange={(e) => setStep4Avatars(prev => prev.map(a => a.id === activeAvatarId ? { ...a, motivations: e.target.value } : a))} className="w-full bg-white border-2 border-gray-100 rounded-xl p-4 focus:ring-0 focus:border-blue-500 font-medium min-h-[120px]" />
                            </div>
                            <div className="space-y-2">
                              <label className="block text-xs font-black text-gray-400 uppercase tracking-widest">{t.step4Challenges}</label>
                              <textarea value={activeAvatar.challenges} onChange={(e) => setStep4Avatars(prev => prev.map(a => a.id === activeAvatarId ? { ...a, challenges: e.target.value } : a))} className="w-full bg-white border-2 border-gray-100 rounded-xl p-4 focus:ring-0 focus:border-blue-500 font-medium min-h-[120px]" />
                            </div>
                            <div className="flex items-center justify-between pt-4">
                              <button onClick={() => handleDeleteAvatar(activeAvatarId)} className="text-red-500 font-black text-sm hover:underline flex items-center space-x-2"><SafeIcon icon={FiTrash2} /><span>{t.step4Delete}</span></button>
                              <button onClick={handleSaveAvatar} className={`flex items-center space-x-2 px-10 py-4 rounded-full font-black transition-all ${isSavedStep4 ? 'bg-green-500 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                                {isSavingStep4 ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={isSavedStep4 ? FiCheckCircle : FiSave} />}<span>{isSavingStep4 ? t.step4Saving : (isSavedStep4 ? t.step4Saved : t.step4Save)}</span>
                              </button>
                            </div>
                          </motion.div>
                        ) : <div className="text-center py-20 bg-gray-50 rounded-[2.5rem] border-2 border-dashed border-gray-200"><p className="text-gray-400 font-bold italic">{lang === 'de' ? 'Füge einen Avatar hinzu, um zu beginnen' : 'Add an avatar to get started'}</p></div>}
                      </AnimatePresence>
                    </div>
                  </div>
                )}

                {/* Step 5 Tool - Blue Ocean Intro & Link */}
                {stepId === 5 && (
                  <div className="bg-white p-8 md:p-12 shadow-sm border border-gray-100 rounded-[2.5rem] overflow-hidden relative group">
                    <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none transition-transform group-hover:scale-110 duration-700">
                      <SafeIcon icon={FiZap} className="text-[180px] text-blue-600" />
                    </div>
                    
                    <div className="relative z-10">
                      <div className="flex items-center space-x-4 mb-8">
                        <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-200">
                          <SafeIcon icon={FiZap} className="text-white text-2xl" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step5ToolTitle}</h3>
                          <div className="h-1 w-12 bg-blue-600 rounded-full mt-1"></div>
                        </div>
                      </div>

                      <div className="bg-blue-50/50 p-8 md:p-10 rounded-[2rem] border border-blue-100 mb-10">
                        <p className="text-xl text-gray-700 leading-relaxed font-medium italic">
                          {t.step5Intro}
                        </p>
                      </div>

                      <div className="flex flex-col sm:flex-row items-center gap-6">
                        <Link 
                          to={p.differentiation}
                          className="w-full sm:w-auto inline-flex items-center justify-center space-x-3 px-10 py-5 bg-blue-600 text-white rounded-2xl font-black hover:bg-blue-700 transition-all shadow-xl shadow-blue-200 hover:shadow-blue-300 active:scale-95 group/btn"
                        >
                          <SafeIcon icon={FiTarget} className="text-xl group-hover/btn:rotate-12 transition-transform" />
                          <span>{t.step5CTA}</span>
                          <SafeIcon icon={FiArrowRight} className="group-hover/btn:translate-x-1 transition-transform" />
                        </Link>
                        
                        <p className="text-sm font-bold text-gray-400 italic">
                          {lang === 'de' ? 'Analysiere deinen Markt und finde deine Nische.' : 'Analyze your market and find your niche.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 7 Tool */}
                {stepId === 7 && (
                  <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem]">
                    <div className="flex items-center space-x-3 mb-8"><div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center"><SafeIcon icon={FiLayers} className="text-blue-600 text-xl" /></div><h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step7ToolTitle}</h3></div>
                    <div className="mb-10 p-6 bg-blue-50 rounded-3xl border border-blue-100 italic text-gray-700">{t.step7ExplanatoryText}</div>
                    <div className="space-y-6">
                      {['product_description', 'target_group', 'goal', 'shortcut'].map(field => (
                        <div key={field} className="space-y-2">
                          <label className="block text-sm font-black text-gray-900 uppercase tracking-widest">{t[`step7Label${field.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('')}`] || field}</label>
                          <textarea value={step7Data[field] || ''} onChange={(e) => setStep7Data({ ...step7Data, [field]: e.target.value })} placeholder={t[`step7Placeholder${field.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('')}`]} className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl p-4 focus:border-blue-500 focus:ring-0 transition-all min-h-[100px] font-medium text-gray-700" />
                        </div>
                      ))}
                      <div className="pt-4 flex items-center justify-end">
                        <button onClick={handleStep7Save} className={`flex items-center space-x-2 px-10 py-4 rounded-full font-black transition-all ${isSavedStep7 ? 'bg-green-500 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                          {isSavingStep7 ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={isSavedStep7 ? FiCheckCircle : FiSave} />}<span>{isSavingStep7 ? t.step7Saving : (isSavedStep7 ? t.step7Saved : t.step7Save)}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {!hasUnlockedAccess && renderLockedOverlay()}
            </div>
          </div>

          <div className="space-y-8">
            <div className="bg-gray-900 rounded-[2.5rem] p-8 text-white shadow-xl sticky top-24 border border-gray-800">
              <div className="flex items-center space-x-3 mb-6"><SafeIcon icon={FiTarget} className="text-blue-400 text-xl" /><h3 className="font-black uppercase tracking-widest text-sm">{t.keyFocus}</h3></div>
              <ul className="space-y-4 mb-8">{step.keyPoints?.map((point, i) => (<li key={i} className="flex items-center space-x-3 p-4 rounded-2xl bg-white/5 border border-white/5 transition-all text-gray-300"><SafeIcon icon={FiArrowRight} className="text-blue-400 text-xs flex-shrink-0" /><span className="font-bold text-sm">{point}</span></li>))}</ul>
              <div className="grid grid-cols-2 gap-3 pt-6 border-t border-white/10">
                {stepId > 1 ? <button onClick={() => navigate(`/steps/${stepId - 1}`)} className="flex items-center justify-center space-x-2 p-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all text-gray-300 font-bold text-sm group"><SafeIcon icon={FiArrowLeft} className="transition-transform group-hover:-translate-x-1" /><span>{t.prevStep}</span></button> : <div />}
                {stepId < 10 ? <button onClick={() => navigate(`/steps/${stepId + 1}`)} className="flex items-center justify-center space-x-2 p-4 rounded-2xl bg-blue-600 hover:bg-blue-700 transition-all text-white font-bold text-sm group"><span>{t.nextStep}</span><SafeIcon icon={FiArrowRight} className="transition-transform group-hover:translate-x-1" /></button> : <div />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepDetail;